import { takeLatest, put, select, all, delay, call } from 'redux-saga/effects';
import { cloneDeep, get } from 'lodash';
import moment from 'moment';
import { message } from 'antd';
import axios from 'axios';
import { dashboardsSelector } from '@store/tempDashboard/selectors';
import { applyFilter, updateInterval } from '@store/common/actions';
import { resetDrillDown } from '@store/summaryContainer/drilldown/actions';
import { setMetrics, selectedMetricChart } from '@store/metricsChartContainer/actions';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { getFilterFromRules } from '@utils';
import { changeMode } from '@store/trends/actions';
import { setSelectedAiAlert, setAllExceptAnomalyPayload } from '@store/trends/aiAlerts/actions';
import { AI_ALERT_DIAGNOSTIC_CHART_LABEL_MAP } from '@utils/aiAlerts/mapping';
import {
  setDimensions,
  setDimensionMetrics,
  setSharedState,
  setGlobalOrderByMetric,
} from '@store/trends/base/actions';
import { isAppModule } from '@utils/constants';
import parseDimensions from '@store/trends/utils/parseDimensions';
import {
  aiAlertsDimensionMappingSelector,
  ecoAIAlert2AccessSelector,
} from '@store/common/selectors';
import { querySelector } from '@store/metricsChartContainer/selectors';
import getMetricsData from '@api/metricContainer';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { setSlice, setDefaultSlice } from '@store/trends/birdChart/actions';
import * as types from './types';
import { MODE_AI_ALERT_DIAGNOSTICS } from '../../constants';
import {
  allExceptAnamolyGraphOption,
  baselineGraphOption,
  thresholdGraphLineOption,
  brushLineSeverityData,
  severityBrushLineOption,
  tooltipPosition,
  t7GraphLineOption,
  projectedLineGraphOption,
  pcpGraphOption,
  pcpDropYAxis,
} from './lineGraphOptions';
import getFilterRuleForAiAlertRootCause from './rootCauseFilter';
import { DIMENSION_LIMIT } from '../../base/reducer';
import {
  getBrushLineDataBySeverity,
  getDimensionMappingForAlertType,
  getPcpDropForTooltip,
  getUniqueDevicesForTimeSeries,
  getUniqueDevicesSeries,
} from './utils';
import {
  getInvertedPercentageMetricByKey,
  getSecondaryMetric,
  getPercentageMetricByKey,
} from '@src/utils/metrics/metricHelpers';
import {
  createMetricConfigForMetrics,
  getAiAlertDiagnosticsMetricList,
  getFlowAlertChartMetrics,
  shouldShowImpactedUniques,
} from '@src/components/ai-alerts/utils';
import { formatAIAlertSummary } from '@src/components/ai-alerts/formatter';
import { metricKeys } from '@pulse/metrics';
import {
  AI_ALERT_KPI_BETA_METRICS,
  ECO_DIAGNOSTICS_CHART_METRICS,
  ECO_DIAGNOSTICS_DIMENSION_METRICS,
} from '@src/components/ai-alerts/constants';

const getFilterRuleFromRootCause = (
  rootCause,
  rootCauseDisplayLabel,
  dimensionName,
  dimensionKeys,
  aiAlertDimensionMapping,
) => {
  const rootCauseArray = rootCause.split('&&&');
  const rootCauseLabelArray = rootCauseDisplayLabel.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');
  let filterRuleObject = {};

  // if the rootcause city is not available in the portal db it will cause an error
  const cityError = {
    isCityCodeNotAvailable: false,
    cityName: '',
  };

  const isAllTraffic = rootCauseArray.length && rootCauseArray[0] === 'All Traffic';

  if (isAllTraffic) {
    return {
      filter: {
        name: 'All Traffic',
        rule: [],
      },
      cityError,
    };
  }

  rootCauseArray.forEach((rootCauseItem, index) => {
    const dimension = dimensionNameArray[index];
    const rootCauseLabel = rootCauseLabelArray[index];
    const mgwDimensionMapping = aiAlertDimensionMapping[dimension];

    const { field } = mgwDimensionMapping;
    let { key } = mgwDimensionMapping;

    key = key ? key : field;

    // this code is to notify the user about any missing city
    if (dimension === 'CITY' && !dimensionKeys[rootCauseItem].length) {
      cityError.isCityCodeNotAvailable = true;
      cityError.cityName = rootCauseItem;
    } else {
      const value =
        dimensionKeys[rootCauseItem] === 'Unknown'
          ? 'CONVIVA_INTERNAL_UNKNOWN'
          : dimensionKeys[rootCauseItem];

      const filterRuleForIndex = getFilterRuleForAiAlertRootCause(
        index,
        rootCauseItem,
        rootCauseLabel,
        field,
        value,
        key,
      );
      filterRuleObject = { ...filterRuleObject, ...filterRuleForIndex };
    }
  });

  const filter = getFilterFromRules(filterRuleObject);

  return {
    filter,
    cityError,
  };
};

function* getAllExceptAnamolyTimeSeriesData(metric, aiAlertFilter, kpiConfig) {
  const query = yield select(querySelector);
  const { reqBody } = cloneDeep(query);
  reqBody.queries[0].metrics = [metric];

  // For KPI-based alerts, compute the metric against the alert's own KPI thresholds
  // (from its meta) rather than the globally-selected KPI.
  if (kpiConfig) {
    reqBody.queries[0].options = {
      ...(reqBody.queries[0].options || {}),
      kpiConfig,
    };
  }

  // all traffic except the rootCause
  let allTrafficExceptAnamoly = aiAlertFilter.rule.map((filter) => {
    const updatedFilter = Object.assign({}, ...filter, { op: 'not_eq' }, { _order: 0 });
    return updatedFilter;
  });

  // all traffic execept all traffic is always 0
  if (aiAlertFilter.name === 'All Traffic') {
    allTrafficExceptAnamoly = [
      {
        field: 'All Traffic',
        op: 'eq',
        value: 'All Traffic',
        key: '',
        display: ' All Traffic',
        _order: 0,
      },
    ];
  }

  // if in App and All Traffic, we set filter to []
  reqBody.queries[0].filter =
    isAppModule && aiAlertFilter.name === 'All Traffic'
      ? // IF server will have a special logic handle OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC
        [
          [
            {
              field: 'app_id',
              op: 'eq',
              value: 'OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC',
              key: '',
              display: 'OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC',
              _order: 0,
            },
          ],
        ]
      : [allTrafficExceptAnamoly];

  yield put(setAllExceptAnomalyPayload(reqBody));

  const source = axios.CancelToken.source();
  const resp = yield call(getMetricsData, reqBody, { cancelToken: source.token });
  const { data } = resp.data[0];
  const aiSeriesData = data.map((item) => [item.timestamp, item.results[metric]]);

  return aiSeriesData;
}

function* getGraphData(selectedAiAlert, aiAlertFilter, isAdAlert = false, showUniqueDevicesSeries) {
  const { diagnosticsData, alertEvents, metricName, startGraphTime, metricUnit } = selectedAiAlert;
  const diagnosticsAlertData = diagnosticsData && diagnosticsData.data;
  const thresholdData = [];
  const baselineData = [];
  // used in App AI Alert for ACTIVE_DEVICES metric
  const projectedData = [];
  const series = [];
  const tMinus7Data = [];
  const pcpDropData = [];
  const uniqueDevices = showUniqueDevicesSeries
    ? getUniqueDevicesForTimeSeries(diagnosticsAlertData)
    : [];
  const pcpDrop = getPcpDropForTooltip(diagnosticsAlertData);
  const brushLineDataForSeverity = getBrushLineDataBySeverity(alertEvents, metricUnit, metricName);

  const percentageMetric = getPercentageMetricByKey({
    metricKey: metricName,
    defaultValue: metricName,
  });
  // KPI-based alerts carry their own KPI config in meta (kpiID + thresholds); pass it so the
  // "all except anomaly" series is computed against the same thresholds as the alert.
  const diagnosticsKpiConfig =
    selectedAiAlert?.meta?.kpiID != null ? selectedAiAlert.meta : undefined;
  const aiTimeSeries = yield getAllExceptAnamolyTimeSeriesData(
    percentageMetric,
    aiAlertFilter,
    diagnosticsKpiConfig,
  );
  // other metric values should be shown in percentage. vst is shown as it is
  // const graphConverter = [
  //   'AD_AUTOALERTBITRATE',
  //   'AUTOALERTVST',
  //   'AUTOALERTPCP',
  //   'APP_SCREEN_LOAD_TIME',
  //   'APP_NETWORK_REQUEST_TIME',
  //   'APP_ACTIVE_DEVICES',
  // ].includes(aiAlertKey)
  //   ? 1
  //   : 100;

  const graphConverter = [
    metricKeys.VideoStartFailuresTechPercentage, //'AUTOALERTVSF',
    metricKeys.ExitBeforeVideoStartsPercentage, //'AUTOALERTEBVS',
    metricKeys.ConnectionInducedRebufferingRatio, //'AUTOALERTCIRR',
    metricKeys.VideoPlaybackFailuresTechPercentage, //'AUTOALERTVPF',
    metricKeys.AdVideoStartFailuresPercentage, //'AD_AUTOALERTVSF',
    metricKeys.AdVideoPlaybackFailuresPercentage, //'AD_AUTOALERTVPF',
    metricKeys.AdRebufferingRatio, // 'AD_AUTOALERTBUFFERINGRATIO',
    metricKeys.AdSlateDurationPct, // 'AD_AUTOALERTSLATEDURATIONRATIO' - server sends fraction (0.2), show as 20%
    // KPI-based Beta metrics: server sends threshold/baseline as fractions (e.g. 0.05),
    // so they need the same ×100 scaling as the other percentage alert metrics above.
    metricKeys.HighExitBeforeVideoStartPercentage, //'AUTOALERTHIGHEBVS',
    metricKeys.HighStartupTimePercentage, //'AUTOALERTHIGHVST',
    metricKeys.HighRebufferingPercentage, //'AUTOALERTHIGHRBR',
    metricKeys.LowBitratePercentage, //'AUTOALERTLOWBITRATE',
  ].includes(metricName)
    ? 100
    : 1;

  diagnosticsAlertData.forEach((currentData) => {
    const threshold = [currentData.timestamp, currentData.results.threshold * graphConverter];
    const baseline = [currentData.timestamp, currentData.results.baseline * graphConverter];
    const tMinus7 = [currentData.timestamp, currentData.results.tMinus7 * graphConverter];
    const pcpDropD = [currentData.timestamp, currentData.results.pcpDrop * graphConverter];
    const projected = [currentData.timestamp, currentData.results.projected * graphConverter];
    thresholdData.push(threshold);
    baselineData.push(baseline);
    tMinus7Data.push(tMinus7);
    pcpDropData.push(pcpDropD);
    projectedData.push(projected);
  });
  // add issue start to the front
  // in case of overlapping events with same time, a second event like info should override start event
  brushLineDataForSeverity.unshift(brushLineSeverityData(startGraphTime));

  if (metricName === metricKeys.ConcurrentPlays) {
    series.push(t7GraphLineOption(tMinus7Data));
    series.push(projectedLineGraphOption(projectedData));
    series.push(
      pcpGraphOption(
        pcpDropData,
        AI_ALERT_DIAGNOSTIC_CHART_LABEL_MAP[window.conviva.module]?.PCP_OR_PCT,
      ),
    );
  } else {
    series.push(baselineGraphOption(baselineData));
    if (!isAdAlert) {
      series.push(allExceptAnamolyGraphOption(aiTimeSeries));
      series.push(thresholdGraphLineOption(thresholdData));
    }
  }

  return {
    tooltipPosition,
    uniqueDevices,
    pcpDrop,
    severityBrushLine: severityBrushLineOption(brushLineDataForSeverity),
    graphSeries: series,
    // TODO: check if CUID is null in data set then this is not required
    uniqueDevicesSeries: showUniqueDevicesSeries
      ? getUniqueDevicesSeries(diagnosticsAlertData)
      : [],
  };
}

function* executeTasks(
  startTimestamp,
  endTimestamp,
  metricName,
  filter,
  selectedAiAlert,
  dimensions,
  dimensionMetrics,
) {
  const { startGraphTime, alertEvents } = selectedAiAlert;
  const endedAlertEvent = alertEvents.find((alert) => alert.eventType === 'Ended');
  const sliceStartTime = new Date(startGraphTime).getTime();
  const sliceEndTime = endedAlertEvent
    ? new Date(endedAlertEvent.graphTime).getTime()
    : new Date(endTimestamp).getTime();
  const secondaryMetric: string = getSecondaryMetric({
    metricKey: metricName,
    defaultValue: metricName,
  });
  const fallbackList = Array.from(
    new Set([
      metricName,
      secondaryMetric,
      ...getFlowAlertChartMetrics(metricName),
      ...(ECO_DIAGNOSTICS_CHART_METRICS[metricName] || []),
    ]),
  );
  // KPI alerts: use the active count metric (same invert as getAlertMetricInfo)
  // plus base QoE + secondary. Selecting the inactive % twin hides the graph.
  const alertMetricName = selectedAiAlert?.metricName;
  const isKpiAlert = AI_ALERT_KPI_BETA_METRICS.includes(alertMetricName);
  const metricList = getAiAlertDiagnosticsMetricList(alertMetricName, fallbackList);
  const chartMetricName = metricList[0] ?? metricName;

  const dimensionsList = yield select(dimensionsListSelectorKeyValue);
  const filteredDimension = parseDimensions(dimensions, dimensionsList);

  // adding an extra minute as MGW excludes last timestamp causing chart series alignment issue
  const updatedEndTimestamp = moment(endTimestamp).add(1, 'minute').format();

  const tasks = [
    put(changeMode(MODE_AI_ALERT_DIAGNOSTICS)),
    put(updateInterval(startTimestamp, updatedEndTimestamp, 'custom', 'ai-alerts')),
    put(setDefaultSlice([0, 100], [sliceStartTime, sliceEndTime])),
    put(setSlice([0, 100], [sliceStartTime, sliceEndTime])),
    put(resetDrillDown([])),
    put(pushDrilldownFilter(filter.name, filter.rule)),
    put(applyFilter(filter)),
    put(setSharedState({})), // resets base state
    put(selectedMetricChart(chartMetricName)),
    put(setMetrics(metricList)),
    put(setDimensions(filteredDimension)),
  ];

  yield all(tasks);
  yield delay(300);
  // Set after the delay so this wins the race against selectedMetricChart's
  // primaryMetricEffects (which would otherwise put secondary-then-alerted,
  // e.g. Ended Plays ahead of Low Bitrate).
  const tableMetrics = isKpiAlert
    ? metricList
    : Array.isArray(dimensionMetrics)
      ? [...new Set([metricName, ...dimensionMetrics])]
      : null;
  if (tableMetrics) {
    yield put(setDimensionMetrics(tableMetrics));
    if (isKpiAlert && tableMetrics.length > 1) {
      yield put(setGlobalOrderByMetric(tableMetrics[tableMetrics.length - 1]));
    }
  }
}

function setCityError(cityError) {
  if (cityError.isCityCodeNotAvailable) {
    message.error({
      key: 'cityError',
      content: `${cityError.cityName} city filter is not supported. Please contact Conviva support team`,
      duration: 10,
      onClick: () => message.destroy('cityError'),
    });
  }
}

const getDimensionsListForAlert = (
  metricDetails,
  dimensionName,
  dimensionMapping,
): { dimensions: string[]; dimensionMetrics: string[] } => {
  const dimensionNameArray = dimensionName.split('&&&');
  const filterDimensions = dimensionNameArray.map(
    (d) => dimensionMapping[d].key || dimensionMapping[d].field,
  );
  const dimensions = [
    ...new Set([...filterDimensions, ...(metricDetails?.dimensionList || [])]),
  ].slice(0, DIMENSION_LIMIT.max);

  const dimensionMetrics = ECO_DIAGNOSTICS_DIMENSION_METRICS[metricDetails.name];

  return { dimensions, dimensionMetrics };
};

export const getAlertMetricInfo = (metric) => {
  const lookupMetric = getInvertedPercentageMetricByKey({
    metricKey: metric,
    defaultValue: metric,
  });
  return lookupMetric ?? metric;
};

function* initAiAlertsDiagnostics(dashboard) {
  try {
    const aiAlerts = get(dashboard, 'module:trends.aiAlerts');
    let selectedAiAlert: any = {};
    const { selectedAlertId, selectedAlertType } = aiAlerts;
    const isAdAlert = selectedAlertType && selectedAlertType === 'AD_ALERT';
    const aiAlert = aiAlerts.selectedAiAlert ? aiAlerts.selectedAiAlert : null;

    const dimensionMapping = yield select(aiAlertsDimensionMappingSelector);
    const dimensionMappingForAlertType = getDimensionMappingForAlertType(
      selectedAlertType,
      dimensionMapping,
    );

    const ecoAIAlert2 = yield select(ecoAIAlert2AccessSelector as any);

    if (!aiAlert) {
      const dimensionListObject = yield select(dimensionsListSelectorKeyValue);
      const diagnosticsData = get(dashboard, 'module:aiAlerts.selectedAiAlertDiagnostics');

      const aiAlertFromDiagnosticDataResponse =
        formatAIAlertSummary(
          [diagnosticsData.summary],
          dimensionListObject,
          dimensionMappingForAlertType,
        )[0] || {};

      selectedAiAlert = { ...aiAlertFromDiagnosticDataResponse };
      selectedAiAlert.diagnosticsData = diagnosticsData;
      const { summary, dimensionKeys, data } = diagnosticsData;
      const { rootCause, dimensionName, rootCauseDisplayLabel } = summary;

      const { cityError, filter } = getFilterRuleFromRootCause(
        rootCause,
        rootCauseDisplayLabel,
        dimensionName,
        dimensionKeys,
        dimensionMappingForAlertType,
      );

      // get the start and end timestamp for the timeseries from the ai alert diagnostics api
      const startTimestamp = data[0].timestamp;
      const endTimestamp = data[data.length - 1].timestamp;
      const metricDetails = createMetricConfigForMetrics([selectedAiAlert.metricName])[0] ?? {};

      const { dimensions, dimensionMetrics } = getDimensionsListForAlert(
        metricDetails,
        dimensionName,
        dimensionMappingForAlertType,
      );

      const metric = getAlertMetricInfo(metricDetails?.name ?? '');

      yield executeTasks(
        startTimestamp,
        endTimestamp,
        metric,
        filter,
        selectedAiAlert,
        dimensions,
        dimensionMetrics,
      );

      const showImpactedUniquesSeries = shouldShowImpactedUniques(
        ecoAIAlert2,
        selectedAiAlert.metricName,
      );
      // check if if-server sends null is this required?
      const graphSeriesData = yield getGraphData(
        selectedAiAlert,
        filter,
        isAdAlert,
        showImpactedUniquesSeries,
      );
      const { severityBrushLine, graphSeries, uniqueDevices, pcpDrop, uniqueDevicesSeries } =
        graphSeriesData;

      selectedAiAlert.startTimestamp = startTimestamp;
      selectedAiAlert.endTimestamp = endTimestamp;
      selectedAiAlert.filter = filter;
      selectedAiAlert.graphSeries = graphSeries;
      selectedAiAlert.severityBrushLine = severityBrushLine;
      selectedAiAlert.uniqueDevices = uniqueDevices;
      selectedAiAlert.uniqueDevicesSeries = uniqueDevicesSeries;
      selectedAiAlert.pcpDrop = pcpDrop;
      selectedAiAlert.pcpDropYAxis = pcpDropYAxis;
      selectedAiAlert.cityError = cityError;

      yield put(setSelectedAiAlert({ selectedAiAlert, selectedAlertId, selectedAlertType }));
      setCityError(cityError);

      yield put({ type: types.END_FETCHING });
    } else {
      const { startTimestamp, endTimestamp, filter, cityError, metricName, diagnosticsData } =
        aiAlert;
      const metricDetails = createMetricConfigForMetrics([selectedAiAlert.metricName])[0] ?? {};

      const { dimensions, dimensionMetrics } = getDimensionsListForAlert(
        metricDetails,
        diagnosticsData.summary.dimensionName,
        dimensionMappingForAlertType,
      );

      const metric = getAlertMetricInfo(metricName ?? '');

      yield executeTasks(
        startTimestamp,
        endTimestamp,
        metric,
        filter,
        selectedAiAlert,
        dimensions,
        dimensionMetrics,
      );
      setCityError(cityError);
      yield put({ type: types.END_FETCHING });
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* initTempDashboard({ tempDashboardId }) {
  const dashboards = yield select(dashboardsSelector);
  const tempDashboard = dashboards[tempDashboardId];

  if (!tempDashboard || !tempDashboard.dashboard) {
    return;
  }

  yield put({ type: types.START_FETCHING });
  const { dashboard } = tempDashboard;
  yield initAiAlertsDiagnostics(dashboard);
}

function* watchInitTempDashboard() {
  yield takeLatest([types.INIT_TEMP_DASHBOARD], initTempDashboard);
}

export default [watchInitTempDashboard];
